import type { CommandSpec } from './registry.js';

/**
 * Keys that views still bind in their own keydown handlers (SceneTab,
 * ModelTab, PixelTab, SoundTab, Hierarchy). They are declared here as
 * `external` so the Shortcut Manager lists the whole keymap, conflict checks
 * see them, and a global binding on the same chord yields to them inside that
 * view (Q is Select in the Scene view, Quick Favorites elsewhere).
 *
 * When a view moves its handler onto the registry (`useCommands` with the
 * same ids and `run` functions), delete its block here: the ids stay the same,
 * so keymap profiles and user overrides carry over.
 */

function declare(context: string, category: string, rows: ReadonlyArray<[id: string, label: string, keys: string[]]>): CommandSpec[] {
  return rows.map(([id, label, keys]) => ({ id: `${context}.${id}`, label, category, context, keys, external: true }));
}

const TRANSFORM_TOOLS: ReadonlyArray<[string, string, string[]]> = [
  ['tool.select', 'Select tool', ['Q']],
  ['tool.move', 'Move tool', ['W']],
  ['tool.rotate', 'Rotate tool', ['E']],
  ['tool.scale', 'Scale tool', ['R']],
  ['space.toggle', 'Toggle world / local space', ['X']],
  ['frame', 'Frame selected', ['F']],
  ['duplicate', 'Duplicate', ['Ctrl+D']],
  ['snap.toggle', 'Toggle snapping', ['Ctrl+G']],
  ['delete', 'Delete selection', ['Delete', 'Backspace']],
  ['deselect', 'Clear selection', ['Escape']],
];

export const LEGACY_COMMANDS: readonly CommandSpec[] = [
  ...declare('scene', 'Scene', [...TRANSFORM_TOOLS, ['rename', 'Rename entity', ['F2']]]),
  ...declare('model', 'Model', [
    ...TRANSFORM_TOOLS,
    ['wireframe.toggle', 'Toggle wireframe', ['Shift+Z']],
    ['mesh-edit.toggle', 'Toggle mesh edit mode', ['Tab']],
  ]),
  ...declare('model.mesh', 'Model · Mesh Edit', [
    ['select-all', 'Select all', ['A']],
    ['extrude', 'Extrude', ['Shift+E']],
    ['inset', 'Inset', ['I']],
    ['merge', 'Merge', ['M']],
    ['crease', 'Crease', ['C']],
    ['flip', 'Flip normals', ['Shift+N']],
    ['mode.vertex', 'Vertex select', ['1']],
    ['mode.face', 'Face select', ['2', '3']],
  ]),
  ...declare('pixel', 'Pixel', [
    ['tool.pencil', 'Pencil', ['B']],
    ['tool.eraser', 'Eraser', ['E']],
    ['tool.fill', 'Fill', ['G']],
    ['tool.line', 'Line', ['L']],
    ['tool.rect', 'Rectangle', ['U']],
    ['tool.ellipse', 'Ellipse', ['O']],
    ['tool.picker', 'Color picker', ['I']],
    ['tool.select', 'Select', ['M']],
    ['tool.move', 'Move', ['V']],
    ['swap-colors', 'Swap colors', ['X']],
    ['frame.prev', 'Previous frame', [',', 'Left']],
    ['frame.next', 'Next frame', ['.', 'Right']],
    ['brush.smaller', 'Smaller brush', ['[']],
    ['brush.bigger', 'Bigger brush', [']']],
    ['zoom.in', 'Zoom in', ['=']],
    ['zoom.out', 'Zoom out', ['-']],
    ['playback.toggle', 'Play animation', ['Enter']],
    ['copy', 'Copy selection', ['Ctrl+C']],
    ['cut', 'Cut selection', ['Ctrl+X']],
    ['paste', 'Paste', ['Ctrl+V']],
    ['select-all', 'Select all', ['Ctrl+A']],
    ['delete', 'Delete selection', ['Delete', 'Backspace']],
    ['deselect', 'Clear selection', ['Escape']],
  ]),
  ...declare('sound', 'Sound', [
    ['transport.toggle', 'Play / pause', ['Space']],
    ['transport.home', 'Seek to start', ['Home']],
    ['loop.toggle', 'Toggle loop', ['L']],
    ['duplicate', 'Duplicate selection', ['Ctrl+D']],
    ['render', 'Render now', ['Ctrl+Enter']],
    ['select-all', 'Select all notes', ['Ctrl+A']],
    ['delete', 'Delete selection', ['Delete', 'Backspace']],
    ['nudge.left', 'Nudge left', ['Left']],
    ['nudge.right', 'Nudge right', ['Right']],
    ['nudge.up', 'Nudge up', ['Up']],
    ['nudge.down', 'Nudge down', ['Down']],
    ['deselect', 'Clear selection', ['Escape']],
  ]),
];
